import { API_ERROR_CODES, type ApiEnvelope, type ApiErrorBody, type ApiMeta } from "@/types/api";

/**
 * Cliente HTTP unico do app (RT-10).
 *
 * Regras que ele existe para garantir, e que nenhum componente deve repetir:
 *
 *   - `credentials: "include"` sempre. A sessao vive em cookie httpOnly e o
 *     frontend NUNCA le o token — e por isso que um XSS aqui nao vira roubo
 *     de sessao (CLAUDE.md 6).
 *   - CSRF double-submit: le o cookie legivel `ct_csrf` e reenvia no header
 *     X-CSRF-Token em toda mutacao.
 *   - 401 dispara UM refresh e repete a requisicao. "Um" no sentido literal:
 *     varias chamadas paralelas que tomam 401 compartilham a mesma promise,
 *     senao cinco widgets na tela viram cinco refreshes concorrentes — e,
 *     como o refresh e rotativo com deteccao de reuso, isso derrubaria a
 *     sessao do usuario.
 *   - 429 vira um erro tipado com `retryAfter`, para a UI avisar em vez de
 *     martelar (RNF-02.1).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  /** Segundos ate poder tentar de novo. So presente em 429. */
  readonly retryAfter?: number;

  constructor(params: {
    status: number;
    code: string;
    message: string;
    details?: Record<string, unknown>;
    retryAfter?: number;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
    this.retryAfter = params.retryAfter;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isValidation(): boolean {
    return this.code === API_ERROR_CODES.validationFailed;
  }

  /** Erros de validacao no formato do React Hook Form. */
  fieldErrors(): Record<string, string> {
    if (!this.isValidation || !this.details) return {};

    return Object.fromEntries(
      Object.entries(this.details).map(([field, messages]) => [
        field,
        Array.isArray(messages) ? String(messages[0]) : String(messages),
      ]),
    );
  }
}

export const CSRF_COOKIE = "ct_csrf";
export const OPERATOR_CSRF_COOKIE = "ct_op_csrf";
const TENANT_HEADER = "X-Tenant-ID";
const CSRF_HEADER = "X-CSRF-Token";

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export type ApiScope = "user" | "operator";

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  scope?: ApiScope;
  signal?: AbortSignal;
  /** Interno: impede laco infinito de refresh. */
  skipRefresh?: boolean;
}

/** Lido do cookie legivel — nao e credencial: o valor e HMAC(segredo, jti) e
 *  sozinho nao autentica nada. */
export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Tenant da requisicao.
 *
 * Em producao o subdominio resolve sozinho no backend. Em dev o Vite serve
 * em localhost:5173 e faz proxy para :3000, entao o Host chega sem o
 * subdominio da clinica — por isso mandamos o header explicito, derivado do
 * hostname quando ele existe.
 */
export function resolveTenantId(): string | null {
  const override = localStorage.getItem("conecta.tenant");
  if (override) return override;

  const host = window.location.hostname;
  const labels = host.split(".");

  // <slug>.localhost ou <slug>.dominio.tld — mas nao "localhost" sozinho
  // nem "www".
  if (labels.length >= 2 && labels[0] && labels[0] !== "www") {
    return labels[0];
  }

  return null;
}

export function setTenantOverride(slug: string | null): void {
  if (slug) localStorage.setItem("conecta.tenant", slug);
  else localStorage.removeItem("conecta.tenant");
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path, window.location.origin);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return url.pathname + url.search;
}

/**
 * Refresh compartilhado. Enquanto uma renovacao esta em voo, toda outra
 * chamada que tomou 401 espera a MESMA promise em vez de disparar a sua.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Avisa a aplicacao que a sessao acabou, para o router mandar ao login. */
type SessionExpiredHandler = (scope: ApiScope) => void;
let onSessionExpired: SessionExpiredHandler = () => {};

export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

async function refreshSession(scope: ApiScope): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const path = scope === "operator" ? "/api/v1/admin/auth/refresh" : "/api/v1/auth/refresh";

  refreshInFlight = (async () => {
    try {
      const response = await fetch(path, {
        method: "POST",
        credentials: "include",
        headers: buildHeaders({ scope, method: "POST" }),
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Liberado assim que a renovacao termina. Quem estava esperando ja
      // segura a referencia da promise, entao limpar aqui nao corta
      // ninguem — e uma chamada que tome 401 DEPOIS desta renovacao precisa
      // mesmo de uma nova, porque o token dela e anterior.
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** Somente para testes: zera a trava de renovacao entre exemplos. Sem isso,
 *  o estado de modulo vaza de um teste para o seguinte. */
export function __resetRefreshState(): void {
  refreshInFlight = null;
}

function buildHeaders(options: { scope: ApiScope; method: string; hasBody?: boolean; isFormData?: boolean }): HeadersInit {
  const headers: Record<string, string> = { Accept: "application/json" };

  // FormData nunca leva Content-Type manual: o fetch define o boundary do
  // multipart sozinho, e sobrescrever isso quebra o parse no servidor
  // (usado pelo anexo do check-in, RF-04.5).
  if (options.hasBody && !options.isFormData) headers["Content-Type"] = "application/json";

  const tenantId = resolveTenantId();
  if (tenantId && options.scope === "user") headers[TENANT_HEADER] = tenantId;

  if (!SAFE_METHODS.has(options.method)) {
    const cookie = options.scope === "operator" ? OPERATOR_CSRF_COOKIE : CSRF_COOKIE;
    const csrf = readCookie(cookie);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  return headers;
}

async function parseError(response: Response): Promise<ApiError> {
  const retryAfterHeader = response.headers.get("Retry-After");
  const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : undefined;

  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }

  return new ApiError({
    status: response.status,
    code: body?.error?.code ?? `http_${response.status}`,
    message: body?.error?.message ?? "Nao foi possivel completar a operacao. Tente de novo.",
    details: body?.error?.details as Record<string, unknown> | undefined,
    retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
  });
}

export interface ApiResponse<T> {
  data: T;
  meta?: ApiMeta;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
  const method = (options.method ?? "GET").toUpperCase();
  const scope = options.scope ?? "user";
  const hasBody = options.body !== undefined;
  const isFormData = options.body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      credentials: "include",
      headers: buildHeaders({ scope, method, hasBody, isFormData }),
      body: hasBody ? (isFormData ? (options.body as FormData) : JSON.stringify(options.body)) : undefined,
      signal: options.signal,
    });
  } catch (error) {
    // Rede fora do ar, DNS, CORS bloqueado. Nunca vaza como "undefined".
    if (error instanceof DOMException && error.name === "AbortError") throw error;

    throw new ApiError({
      status: 0,
      code: API_ERROR_CODES.network,
      message: "Sem conexao com o servidor. Verifique sua internet e tente de novo.",
    });
  }

  // Sessao expirada: renova uma vez e repete. `skipRefresh` corta o laco
  // quando o proprio refresh (ou o login) devolve 401.
  if (response.status === 401 && !options.skipRefresh) {
    const refreshed = await refreshSession(scope);

    if (refreshed) {
      return apiRequest<T>(path, { ...options, skipRefresh: true });
    }

    onSessionExpired(scope);
    throw await parseError(response);
  }

  if (!response.ok) throw await parseError(response);

  if (response.status === 204) return { data: undefined as T };

  const envelope = (await response.json()) as ApiEnvelope<T>;
  return { data: envelope.data, meta: envelope.meta };
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "GET" }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),

  // Upsert do check-in diario (PUT /daily-records/today): o mesmo endpoint
  // cria ou edita dependendo se o dia ja tem registro, e o backend usa PUT
  // para isso — nao POST/PATCH.
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
};
